import { refs } from '../../core/refs.js';
import {
  getActiveSandboxOrigin,
  postToSandbox,
  setSandboxReady
} from './transport.js';

const MESSAGE_TYPES = new Set([
  'active-route',
  'console',
  'front-my-end-inspect-result',
  'preview-contextmenu',
  'preview-navigate',
  'preview-render-complete',
  'preview-scroll',
  'sandbox-ready',
  'preview-escape'
]);

function isObject(value) {
  return value !== null && typeof value === 'object';
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function isStringArray(value) {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function validateConsole(data) {
  return typeof data.level === 'string' && typeof data.message === 'string';
}

function validateActiveRoute(data) {
  return typeof data.hash === 'string' && (data.hash === '' || data.hash.startsWith('#'));
}

function validatePreviewNavigate(data) {
  return typeof data.href === 'string' && (!data.fromPath || typeof data.fromPath === 'string');
}

function validatePreviewContextMenu(data) {
  return isFiniteNumber(data.clientX) && isFiniteNumber(data.clientY);
}

function validatePreviewScroll(data) {
  return isFiniteNumber(data.x) && isFiniteNumber(data.y) && data.x >= 0 && data.y >= 0;
}

function validateInspectResult(data) {
  return (
    typeof data.tagName === 'string' &&
    typeof data.id === 'string' &&
    isStringArray(data.classes) &&
    isStringArray(data.cssSelectors)
  );
}

function validateMessage(data) {
  if (!isObject(data) || !MESSAGE_TYPES.has(data.type)) return false;

  switch (data.type) {
    case 'active-route':
      return validateActiveRoute(data);
    case 'console':
      return validateConsole(data);
    case 'front-my-end-inspect-result':
      return validateInspectResult(data);
    case 'preview-contextmenu':
      return validatePreviewContextMenu(data);
    case 'preview-navigate':
      return validatePreviewNavigate(data);
    case 'preview-render-complete':
      return data.timingId === undefined || isFiniteNumber(data.timingId);
    case 'preview-scroll':
      return validatePreviewScroll(data);
    case 'sandbox-ready':
      return true;
    case 'preview-escape':
      return true;
    default:
      return false;
  }
}

class PreviewBridge {
  constructor() {
    this.handlers = {};
    this.boundMessageHandler = this.handleMessage.bind(this);
    this.started = false;
  }

  start(handlers = {}) {
    this.handlers = handlers;
    if (this.started) return;
    this.started = true;
    window.addEventListener('message', this.boundMessageHandler);
  }

  stop() {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener('message', this.boundMessageHandler);
    this.handlers = {};
  }

  post(payload) {
    postToSandbox(payload);
  }

  isTrustedEvent(event) {
    const expectedOrigin = getActiveSandboxOrigin();
    if (!expectedOrigin || event.origin !== expectedOrigin) return false;
    return event.source === refs.previewIframe?.contentWindow;
  }

  handleMessage(event) {
    if (!this.isTrustedEvent(event)) return;

    const data = event.data;
    if (!validateMessage(data)) return;

    switch (data.type) {
      case 'active-route':
        this.handlers.onActiveRoute?.(data);
        break;
      case 'console':
        this.handlers.onConsole?.(data);
        break;
      case 'front-my-end-inspect-result':
        this.handlers.onInspectResult?.(data);
        break;
      case 'preview-contextmenu':
        this.handlers.onPreviewContextMenu?.(data);
        break;
      case 'preview-navigate':
        this.handlers.onPreviewNavigate?.(data);
        break;
      case 'preview-render-complete':
        this.handlers.onPreviewRenderComplete?.(data);
        break;
      case 'preview-scroll':
        this.handlers.onPreviewScroll?.(data);
        break;
      case 'sandbox-ready':
        setSandboxReady(true);
        this.handlers.onSandboxReady?.(data);
        break;
      case 'preview-escape':
        this.handlers.onPreviewEscape?.(data);
        break;
      default:
        break;
    }
  }
}

export const previewBridge = new PreviewBridge();
